import { useState, useCallback, useEffect } from 'react';

// Глобальный кэш для всех страниц
const globalCache = new Map();
const globalLoadingStates = new Map();
const listeners = new Set();

// Функция для уведомления всех слушателей об изменении состояния
const notifyListeners = () => {
  listeners.forEach(listener => listener());
};

// Отслеживание активных запросов для предотвращения race conditions
const currentRequests = new Map();

// Загрузить данные и сохранить в кэш
const loadData = async (cacheKey, loadFunction) => {
  // Генерируем уникальный ID для этого запроса
  const requestId = Date.now() + Math.random();
  currentRequests.set(cacheKey, requestId);

  globalLoadingStates.set(cacheKey, true);
  notifyListeners();

  try {
    const result = await loadFunction();

    // Проверяем, является ли этот запрос всё ещё актуальным (последним)
    if (currentRequests.get(cacheKey) === requestId) {
      globalCache.set(cacheKey, result);
      globalLoadingStates.set(cacheKey, false);
      notifyListeners();
    }
  } catch (error) {
    // Обрабатываем ошибку только если запрос актуален
    if (currentRequests.get(cacheKey) === requestId) {
      console.error(`Error loading data for ${cacheKey}:`, error);
      globalLoadingStates.set(cacheKey, false);
      notifyListeners();
    }
  }
};

// Принудительно обновить данные
const refreshData = async (cacheKey, loadFunction) => {
  globalCache.delete(cacheKey);
  await loadData(cacheKey, loadFunction);
};

/**
 * Хук для кэширования данных страниц
 * Предотвращает повторную загрузку данных при переключении между страницами
 */
export const usePageCache = () => {
  const [, forceUpdate] = useState({});

  // Подписываемся на изменения глобального состояния
  useEffect(() => {
    const listener = () => forceUpdate({});
    listeners.add(listener);
    return () => listeners.delete(listener);
  }, []);

  /**
   * Получить кэшированные данные для страницы
   * @param {string} pageKey - Уникальный ключ страницы
   * @param {Function} loadFunction - Функция загрузки данных
   * @param {Array} dependencies - Зависимости для перезагрузки
   * @returns {Object} - Объект с данными, состоянием загрузки и функцией обновления
   */
  const getCachedData = useCallback((pageKey, loadFunction, dependencies = []) => {
    const cacheKey = `${pageKey}_${JSON.stringify(dependencies)}`;
    const cachedData = globalCache.get(cacheKey);
    const isLoading = globalLoadingStates.get(cacheKey) || false;

    // Если данные уже загружены и не загружаются, возвращаем кэш
    if (cachedData && !isLoading) {
      return {
        data: cachedData,
        loading: false,
        error: null,
        refresh: () => refreshData(cacheKey, loadFunction)
      };
    }

    // Если данные не загружены и не загружаются, запускаем загрузку
    if (!cachedData && !isLoading) {
      loadData(cacheKey, loadFunction);
    }

    return {
      data: cachedData || null,
      loading: isLoading,
      error: null,
      refresh: () => refreshData(cacheKey, loadFunction)
    };
  }, []);

  /**
   * Очистить кэш для конкретной страницы
   */
  const clearPageCache = useCallback((pageKey) => {
    const keysToDelete = Array.from(globalCache.keys()).filter(key =>
      key.startsWith(pageKey)
    );
    keysToDelete.forEach(key => {
      globalCache.delete(key);
      globalLoadingStates.delete(key);
    });
    notifyListeners();
  }, []);

  /**
   * Очистить весь кэш
   */
  const clearAllCache = useCallback(() => {
    globalCache.clear();
    globalLoadingStates.clear();
    notifyListeners();
  }, []);

  return {
    getCachedData,
    clearPageCache,
    clearAllCache
  };
};

/**
 * Инвалидировать кеш для конкретной страницы (можно вызывать извне)
 * @param {string} pageKey - Ключ страницы для очистки кеша
 */
export const invalidatePageCache = (pageKey) => {
  const keysToDelete = Array.from(globalCache.keys()).filter(key =>
    key.startsWith(pageKey)
  );
  keysToDelete.forEach(key => {
    globalCache.delete(key);
    globalLoadingStates.delete(key);
  });
  notifyListeners();
};

